import Decimal from "decimal.js";
import { MarginWarning, StepWarning } from "./warnings";

// ---------------------------------------------------------------------------
// Number / range formatters
// ---------------------------------------------------------------------------

/**
 * Format a Decimal as a signed USD string with thousand separators and two
 * decimal places, e.g. `$1,500.00`, `-$1,750.00`. Decimal-only math; never
 * coerces to JS number.
 */
export function formatSignedUsd(value: Decimal): string {
  const abs = value.abs().toFixed(2);
  const withCommas = abs.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return value.isNegative() ? `-$${withCommas}` : `$${withCommas}`;
}

/** Format a Decimal step value with two decimal places (e.g. `7.00`). */
export function formatStepValue(value: Decimal): string {
  return value.toFixed(2);
}

/** Format an inclusive min/max range, e.g. `2.90\u20136.00`. */
export function formatRange(min: Decimal, max: Decimal): string {
  return `${min.toFixed(2)}\u2013${max.toFixed(2)}`;
}

/**
 * For a step warning, format the bounds range that matches its level:
 * - `outside_normal` -> the normal range
 * - `outside_exaggerated` -> the exaggerated range
 */
export function formatStepBoundsRange(w: StepWarning): string {
  if (w.level === "outside_normal") {
    return formatRange(w.bounds.normal.min, w.bounds.normal.max);
  }
  return formatRange(w.bounds.exaggerated.min, w.bounds.exaggerated.max);
}

/** "large" for above-range warnings, "small" for below-range warnings. */
export function stepDirectionSizeWord(w: StepWarning): "large" | "small" {
  return w.direction === "above" ? "large" : "small";
}

// ---------------------------------------------------------------------------
// Copy templates
// ---------------------------------------------------------------------------

/**
 * Inline-tooltip copy for a margin warning. Used by the warning icon shown
 * next to a rendered margin field.
 *
 * Example:
 *   "Margin Amount is $2,134.50, which is at or above the $1,500 review threshold."
 */
export function marginWarningTooltipText(w: MarginWarning): string {
  return `${w.label} is ${formatSignedUsd(w.value)}, which is at or above the ${formatSignedUsd(w.threshold)} review threshold.`;
}

/**
 * Inline-tooltip copy for a step warning. Used by the warning icon shown
 * next to a rendered step field.
 *
 * Examples:
 *   "Step 1 of 7.00 is outside the normal Corn range of 2.90\u20136.00. This seems large for Corn; please confirm."
 *   "Step 1 of 9.00 is outside the Corn bounds of 2.50\u20138.50. Did you mean to select Corn and have Step 1 of 9.00?"
 */
export function stepWarningTooltipText(w: StepWarning): string {
  const valueStr = formatStepValue(w.value);
  const range = formatStepBoundsRange(w);
  if (w.level === "outside_normal") {
    return `${w.label} of ${valueStr} is outside the normal ${w.commodityLabel} range of ${range}. This seems ${stepDirectionSizeWord(w)} for ${w.commodityLabel}; please confirm.`;
  }
  return `${w.label} of ${valueStr} is outside the ${w.commodityLabel} bounds of ${range}. Did you mean to select ${w.commodityLabel} or have ${w.label} of ${valueStr}?`;
}

/**
 * Dialog-row copy for a step warning. Used as a `<li>` line inside the
 * confirmation dialog's step card. Slightly different wording than the
 * tooltip: leads with `"Step 1: 7.00 \u2014 ..."`.
 *
 * Examples:
 *   "Step 1: 7.00 \u2014 outside normal Corn range (2.90\u20136.00). This seems large for Corn; please confirm."
 *   "Step 1: 9.00 \u2014 outside Corn bounds (2.50\u20138.50). Did you mean to select Corn and have Step 1 of 9.00?"
 */
export function stepWarningDialogText(w: StepWarning): string {
  const valueStr = formatStepValue(w.value);
  if (w.level === "outside_normal") {
    return `${w.label}: ${valueStr} \u2014 outside normal ${w.commodityLabel} range (${formatRange(w.bounds.normal.min, w.bounds.normal.max)}). This seems ${stepDirectionSizeWord(w)} for ${w.commodityLabel}; please confirm.`;
  }
  return `${w.label}: ${valueStr} \u2014 outside ${w.commodityLabel} bounds (${formatRange(w.bounds.exaggerated.min, w.bounds.exaggerated.max)}). Did you mean to select ${w.commodityLabel} or have ${w.label} of ${valueStr}?`;
}
